import React, {useEffect} from 'react';
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {DialogActions, LinearProgress, Skeleton, Stack} from "@mui/material";
import {useUpdateCompetitionRegistrationMutation} from "../../slices/apiSlice";
import {
    countryArray,
    getRlPilot,
    isViableRegistrationStatus, searchRlPilotName
} from "../../util/standards-util";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {StyledTableCell, StyledTableRow} from "./EventTable";
import TableBody from "@mui/material/TableBody";
import Tooltip from "@mui/material/Tooltip";
import {findFlagUrlByIso2Code} from 'country-flags-svg';
import {findFlagUrlByIso3Code} from 'country-flags-svg';
import TableContainer from "@mui/material/TableContainer";
import {
    ArrowBack,
    CheckCircleOutline, Edit,
    ErrorOutlineOutlined,
    InfoOutlined,
    WarningAmberOutlined
} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import RankingListPilotSearchWidget from "./RankingListPilotSearchWidget";

/** Presents a button that when clicked refreshes the pilot rankings for the specified evevt
 * from the ranking list.
 * This does not check of authorization rights, the calling function should do so
 * (although the update would ultimately fail at the server side
 *
 * @returns {JSX.Element}
 * @constructor
 */
const RefreshPilotRankingsWidget = ({ event, entries, onClose = ()=>{} }) => {
    const eventId = event.id
    const eventEntryNo = 20

    const [refreshingPilotRankings, setRefreshingPilotRankings] = React.useState(true)
    const [pilotIndex, setPilotIndex] = React.useState(0)
    const [entryArray,setEntryArray] = React.useState(entries)
    const [editRlDialogOpen, setEditRlDialogOpen] = React.useState(false)
    const [rlSuggestions, setRlSuggestions] = React.useState([])
    const [editRlRegId, setEditRlRegId] = React.useState()
    const [searchSelectedData, setSearchSelectedData] = React.useState()

    const [updateRlData, updateRlDataResult] = useUpdateCompetitionRegistrationMutation()


    const fetchNextPilotRanking = () => {
        const details = entryArray[pilotIndex].details
        const  rlId = details.igcRankingId
        if (!isNaN(Number(rlId)) && Number(rlId) > 0) {
            getRlPilot(rlId,(data) => {
                if (data.data) {
                    const ranking = data.data.rankingpos
                    updateRlData({
                        id: details.i,
                        eventId: eventId,
                        rankingPosition: ranking,
                    })

                    const newEntries = [...entryArray]
                    newEntries[pilotIndex].rlData=data.data
                    const newRegistration = {...(newEntries[pilotIndex]).registration}
                    newRegistration.m=ranking
                    newEntries[pilotIndex].registration=newRegistration
                    setEntryArray(newEntries)

                    const rlData = data.data
                    const pilot = entryArray[pilotIndex].pilot
                    const fullMatch = rlData.surname ===pilot.lastname && rlData.firstname ===pilot.firstname

                    if (fullMatch) {
                        setPilotIndex(pilotIndex + 1)
                    }
                    else {
                        searchRlWithSurname()
                    }
                }
                else {
                    searchRlWithSurname()
                }
            },
                (error) => {
                    console.log(`*** ERROR while fetching IGC RL ranking for pilot with RL id = ${rlId}`)
                    console.log(error)
                    setPilotIndex(pilotIndex + 1)
                }
            )
        }
        else {
            searchRlWithSurname()
        }
    }

    const searchRlWithSurname = ()=>{
        const surname = entryArray[pilotIndex].pilot.lastname
        const regId = entryArray[pilotIndex].registration.i
        searchRlPilotName(surname,(data) => {
            if (data.Success && data.Content.length>0) {
                let sug = [...rlSuggestions]
                sug.push( {
                    regId:regId,
                    data:data.Content,
                })
                setRlSuggestions(sug)
            }
                setPilotIndex(pilotIndex + 1)
            },
            (error) => {
                console.log(`*** ERROR while searching IGC RL for surname = ${surname}`)
                console.log(error)
                setPilotIndex(pilotIndex + 1)
            }
        )
    }

    const handleSelectedRlPilot = (data) => {
        console.log('')
        console.log(data)
        setSearchSelectedData(data)
    }

    const editRlNumber = (regId) => {
        const sugs = rlSuggestions.filter(sug=>sug.regId===regId)[0]?.data
        setEditRlRegId(regId)
        setSearchSelectedData(sugs? sugs[0] : undefined)
        setEditRlDialogOpen(true)
    }

    const closeEditRlDialog = (ok) => {
        if (ok && searchSelectedData) {
            const entry = entries.find(entry => entry.registration.i === editRlRegId)
            const ranking = searchSelectedData.rankingpos
            updateRlData({
                id: entry.registration.i,
                eventId: eventId,
                rankingPosition: ranking,
                igcRankingId: searchSelectedData.pilotid,
            })

            const newEntries = [...entryArray]
            const newEntry = {...(entryArray.find(entry=>entry.registration.i===editRlRegId))}
            const newRegistration = {...newEntry.registration}
            const newDetails = {...newEntry.details}
            newRegistration.m=ranking
            newDetails.igcRankingId=searchSelectedData.pilotid
            newEntry.registration=newRegistration
            newEntry.details=newDetails
            setEntryArray(newEntries.map(entry => entry.registration.i === editRlRegId ? newEntry: entry))
        }
        setEditRlDialogOpen(false)
        setSearchSelectedData()
    }

    useEffect(() => {
        if (refreshingPilotRankings) {
            if (pilotIndex < entryArray.length) {
                fetchNextPilotRanking()
            }
            else {
                setRefreshingPilotRankings(false)
            }
        }
    }, [pilotIndex]);

    useEffect(() => {
        fetchNextPilotRanking()
    }, []);

    return (
        <>
            {refreshingPilotRankings &&
                <Dialog
                    open={true}
                >
                    <DialogContent  sx={{
                        minWidth:'26em',
                        minHeight:'25vh',
                        display:'flex',
                        flexDirection:'column',
                        justifyContent:'space-between',
                        alignItems:'center',
                        background:'#333333',
                        color:'#ffffff',
                    }}>
                        <Typography variant={'body1'} component={'div'}>Retrieving pilot rankings from IGC Ranking List</Typography>
                        <Typography variant={'h4'} component={'div'}>{pilotIndex+1}/{entryArray.length}</Typography>
                        <Box sx={{ width: '100%', height:'1em' }}>
                            <LinearProgress color={'warning'}/>
                        </Box>
                    </DialogContent>
                </Dialog>
            }
            {!refreshingPilotRankings &&
                <>
                    <Stack direction={'row'} sx={{alignItems:'center'}}  mb={2}>
                        <IconButton onClick={onClose}><ArrowBack/></IconButton>
                        <Typography variant={'h5'}>IGC Ranking List data</Typography>
                    </Stack>
                <TableContainer component={'div'}>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>#</StyledTableCell>
                                <StyledTableCell>Pilot</StyledTableCell>
                                <StyledTableCell sx={{textAlign:'center'}}>Nationality</StyledTableCell>
                                <StyledTableCell sx={{textAlign: 'right'}}>Ranking</StyledTableCell>
                                <StyledTableCell>Ranking List ID</StyledTableCell>
                                <StyledTableCell>Ranking List Data</StyledTableCell>
                                <StyledTableCell>&nbsp;</StyledTableCell>
                                <StyledTableCell>&nbsp;</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {entryArray.map((entry,idx) => {
                                const reg= entry.registration
                                const pilot = entry.pilot
                                const details = entry.details
                                const rlData = entry.rlData

                                const fullMatch = rlData && rlData.surname ===pilot.lastname && rlData.firstname ===pilot.firstname
                                const partialMatch = rlData && (rlData.surname !==pilot.lastname || rlData.firstname !==pilot.firstname) && !(rlData.surname !==pilot.lastname && rlData.firstname !==pilot.firstname)

                                const sx = idx < eventEntryNo && isViableRegistrationStatus(reg.u) ?
                                    {
                                        '&:last-child td, &:last-child th': {border: 0},
                                        "&:hover": {backgroundColor: "#D3EDF4 !important"},
                                    }
                                    :
                                    {
                                        '&:last-child td, &:last-child th': {border: 0},
                                        '& td, & th': {borderBottom: 'solid 1px #999999'},
                                        "&:hover": {backgroundColor: "#D3EDF4 !important"},
                                        '&:nth-of-type(even)': {
                                            backgroundColor:'#cccccc',
                                        },
                                        '&:nth-of-type(odd)': {
                                            backgroundColor:'#dddddd',
                                        },
                                    }


                                return (
                                    <StyledTableRow
                                        key={reg.i}
                                        sx={sx}
                                    >
                                        <StyledTableCell component="th" scope="row">
                                            {idx < eventEntryNo  && isViableRegistrationStatus(reg.u) ? idx + 1 : '-' }
                                        </StyledTableCell>
                                        <StyledTableCell>{pilot.firstname} {pilot.lastname}</StyledTableCell>
                                        <StyledTableCell sx={{textAlign:'center'}}><Tooltip title={countryArray.find( c => c.key===pilot.nationality).label} arrow><img src={findFlagUrlByIso2Code(pilot.nationality)} style={{height:'1.2em'}}/></Tooltip></StyledTableCell>
                                        <StyledTableCell sx={{textAlign: 'right'}}>{reg.m ? reg.m : '-'}</StyledTableCell>
                                        <StyledTableCell>{details.igcRankingId}</StyledTableCell>
                                        <StyledTableCell>{rlData ? `${rlData.firstname} ${rlData.surname}` : '-'}</StyledTableCell>
                                        <StyledTableCell>{
                                            rlData ?
                                            fullMatch ?
                                                <Box sx={{display:'flex',alignItems:'center'}}><CheckCircleOutline sx={{my:'-2px'}} color={'success'}/></Box>
                                            : partialMatch ?
                                                    <Box sx={{display:'flex',alignItems:'center'}}><WarningAmberOutlined sx={{my:'-2px'}} color={'warning'}/><Typography variant={'body2'} component={'div'} ml={1}  color={'warning'}>Please confirm Ranking List data matches pilot information</Typography></Box>
                                                    :
                                                    <Box sx={{display:'flex',alignItems:'center'}}><ErrorOutlineOutlined sx={{my:'-2px'}}  color={'error'}/><Typography variant={'body2'} component={'div'} ml={1} color={'error'}>Please check ID for match to pilot</Typography></Box>
                                                :
                                                details.igcRankingId && !isNaN(Number(details.igcRankingId)) ?
                                                    ''
                                                    :
                                                    <Box sx={{display:'flex',alignItems:'center'}}><InfoOutlined sx={{my:'-2px'}} color={'warning'}/><Typography variant={'body2'} component={'div'} ml={1} color={'warning'}>ID missing for pilot</Typography></Box>
                                        }
                                        </StyledTableCell>
                                        <StyledTableCell>{!(rlData && fullMatch) ? <IconButton size={'small'} onClick={(e)=>{editRlNumber(reg.i)}} sx={{my:'-5px'}}><Edit/></IconButton> : ''}</StyledTableCell>
                                    </StyledTableRow>
                                )
                            })
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                {editRlDialogOpen &&
                    <Dialog
                        open={true}
                    >
                        <DialogContent>
                            <Box>
                                <RankingListPilotSearchWidget suggestions={rlSuggestions} regId={editRlRegId} onChange={handleSelectedRlPilot}/>
                            </Box>
                            {searchSelectedData &&
                                <Box my={2} sx={{display:'flex',alignItems:'center'}}>
                                    <img src={findFlagUrlByIso3Code(searchSelectedData.nationality)} style={{width:'2.4em',marginRight:'1em'}}/>
                                    {searchSelectedData.firstname} {searchSelectedData.surname}
                                    <Typography variant={'body2'} ml={1} color={'#999999'}>
                                        (ID={searchSelectedData.pilotid})
                                    </Typography>
                                </Box>
                            }
                            {!searchSelectedData &&
                                <Box my={2}>
                                    <Typography color={'#999999'}>Enter name of pilot to search IGC RL</Typography>
                                </Box>

                            }

                        </DialogContent>
                        <DialogActions>
                            <Button size={'small'} variant={'contained'} onClick={()=>{closeEditRlDialog()}}>Cancel</Button>
                            <Button size={'small'} variant={'contained'} onClick={()=>{closeEditRlDialog(true)}}>OK</Button>
                        </DialogActions>
                    </Dialog>
                }
            </>
            }
        </>
    )
}

export default RefreshPilotRankingsWidget;